// Viewport renderer: takes the pipeline's ImageData output and draws it onto
// a display canvas with integer-snap zoom and nearest-neighbor scaling.
//
// Kept deliberately minimal — pan/zoom UI lives in `ui/viewport.ts`.

export type Viewport = {
  zoom: number;       // integer (1, 2, 4, 8, ...) — caller is responsible for snapping
  offsetX: number;    // canvas-pixel offset
  offsetY: number;    // canvas-pixel offset
};

export const DEFAULT_VIEWPORT: Viewport = { zoom: 1, offsetX: 0, offsetY: 0 };

export type RenderTarget = HTMLCanvasElement | OffscreenCanvas;

/**
 * Draws image into target, sized to fit the target's intrinsic pixel dimensions.
 * `image` may be null (clears target).
 */
export function drawToCanvas(
  target: RenderTarget,
  image: ImageData | null,
  viewport: Viewport = DEFAULT_VIEWPORT,
): void {
  const ctx = target.getContext('2d') as
    | CanvasRenderingContext2D
    | OffscreenCanvasRenderingContext2D
    | null;
  if (!ctx) throw new Error('render: target has no 2d context');

  ctx.imageSmoothingEnabled = false;
  ctx.clearRect(0, 0, target.width, target.height);
  if (image === null) return;

  // Put image to a scratch canvas then drawImage with integer scale.
  const scratch = makeScratch(image);
  const w = image.width * viewport.zoom;
  const h = image.height * viewport.zoom;
  ctx.drawImage(scratch, viewport.offsetX, viewport.offsetY, w, h);
}

let scratchCache: { canvas: HTMLCanvasElement; w: number; h: number } | null = null;

function makeScratch(image: ImageData): HTMLCanvasElement {
  if (
    scratchCache === null ||
    scratchCache.w !== image.width ||
    scratchCache.h !== image.height
  ) {
    const canvas = document.createElement('canvas');
    canvas.width = image.width;
    canvas.height = image.height;
    scratchCache = { canvas, w: image.width, h: image.height };
  }
  const sctx = scratchCache.canvas.getContext('2d');
  if (!sctx) throw new Error('render: scratch has no 2d context');
  sctx.putImageData(image, 0, 0);
  return scratchCache.canvas;
}

/** Integer-snap zoom levels for the viewport. */
export const ZOOM_LEVELS: readonly number[] = [1, 2, 3, 4, 6, 8, 12, 16, 24, 32];

export function snapZoom(target: number): number {
  let best = ZOOM_LEVELS[0] as number;
  let bestDist = Math.abs(target - best);
  for (const z of ZOOM_LEVELS) {
    const d = Math.abs(target - z);
    if (d < bestDist) {
      best = z;
      bestDist = d;
    }
  }
  return best;
}

export function nextZoom(current: number): number {
  for (const z of ZOOM_LEVELS) if (z > current) return z;
  return ZOOM_LEVELS[ZOOM_LEVELS.length - 1] as number;
}

export function prevZoom(current: number): number {
  for (let i = ZOOM_LEVELS.length - 1; i >= 0; i--) {
    const z = ZOOM_LEVELS[i] as number;
    if (z < current) return z;
  }
  return ZOOM_LEVELS[0] as number;
}
